import React, { useEffect, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import './MainDashboard.css';
import {
  fetchUsersCount,
  fetchAgenciesCount,
  fetchCentersCount,
  fetchMonthlyUserData,
  fetchMonthlyAgenciesData,
  fetchMonthlyCentersData,
} from '../../API/dashboardApi';

Chart.register(...registerables);


const MainDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    numOfUsers: 0,
    numOfAgencies: 0,
    numOfCenters: 0,
    monthlyUserData: Array(12).fill(0),
    monthlyAgencyData: Array(12).fill(0),
    monthlyCenterData: Array(12).fill(0),
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [
          usersCount,
          agenciesCount,
          centersCount,
          monthlyUserData,
          monthlyAgencyData,
        ] = await Promise.all([
          fetchUsersCount(),
          fetchAgenciesCount(),
          fetchCentersCount(),
          fetchMonthlyUserData(),
          fetchMonthlyAgenciesData(),
        ]);

        // Log API response for debugging
        console.log('Users Count:', usersCount);
        console.log('Agencies Count:', agenciesCount);
        console.log('Centers Count:', centersCount);
        console.log('Monthly User Data:', monthlyUserData);
        console.log('Monthly Agency Data:', monthlyAgencyData);

        const monthlyCenterData = await fetchMonthlyCentersData();
        console.log('Monthly Center Data:', monthlyCenterData);

        // Ensure monthlyAgencyData is in the expected format
        const validMonthlyAgencyData = Array.isArray(monthlyAgencyData) && monthlyAgencyData.length === 12
          ? monthlyAgencyData
          : Array(12).fill(0);

        setDashboardData({
          numOfUsers: usersCount || 0,
          numOfAgencies: agenciesCount || 0,
          numOfCenters: centersCount || 0,
          monthlyUserData: monthlyUserData || Array(12).fill(0),
          monthlyAgencyData: validMonthlyAgencyData,
          monthlyCenterData: monthlyCenterData || Array(12).fill(0),
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
      },
    },
  };

  const createChartData = (data, label, backgroundColor, borderColor) => ({
    labels: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    datasets: [{
      label,
      data,
      backgroundColor,
      borderColor,
      borderWidth: 2,
      fill: false,
    }],
  });

  if (isLoading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="pt-4">
        <Alert variant="danger">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="pt-4 max-width-100">
      <h2 className="text-center mb-4">Afar Tourism Office Dashboard</h2>

      <Row className="mb-4">
        {[ 
          { title: 'Number of Users', value: dashboardData.numOfUsers },
          { title: 'Number of Agencies', value: dashboardData.numOfAgencies },
          { title: 'Number of Tourist Centers', value: dashboardData.numOfCenters },
        ].map((cardData, index) => (
          <Col md={4} key={index}>
          <Card className="text-center  cardsss">
            <Card.Body className='dash-title'>
              <h2 className='dash-title'>{cardData.title}</h2>
              <h4>{cardData.value}</h4>
            </Card.Body>
          </Card>
        </Col>
        ))}
      </Row>

      <Row>
        <Col md={6}>
          <Card className="p-3">
            <Card.Title>Monthly User Registrations</Card.Title>
            <div style={{ height: '300px' }}>
              <Line data={createChartData(dashboardData.monthlyUserData, 'Monthly User Registrations', '#6c757d', '#495057')} options={chartOptions} />
            </div>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3">
            <Card.Title>Monthly Agencies Data</Card.Title>
            {dashboardData.monthlyAgencyData.length === 0 ? (
              <Alert variant="info">No agency data available for this period.</Alert>
            ) : (
              <div style={{ height: '300px' }}>
                <Line data={createChartData(dashboardData.monthlyAgencyData, 'Monthly Agencies Data', '#28a745', '#218838')} options={chartOptions} />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MainDashboard;